import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";

const faqItems = [
  {
    q: "Is Bellhopt Just Grocery Delivery?",
    a: "No. Bellhopt combines pre-arrival ordering, delivery, in-home stocking, and arrival presentation so the home feels ready before the guest walks in.",
  },
  {
    q: "Who Handles The Orders And In-Home Execution?",
    a: "Bellhopt uses in-house team members who are trained, vetted, and managed to Bellhopt standards. Hosts are not relying on anonymous gig labor for the final arrival experience.",
  },
  {
    q: "Why Does This Matter For Hosts And Property Managers?",
    a: "Trust matters when someone is entering a home before check-in. Bellhopt is built to give operators a reliable team, consistent execution, and a five-star arrival standard across the portfolio.",
  },
  {
    q: "How Do You Keep Execution Consistent?",
    a: "Bellhopt works from property instructions, access notes, reservation timing, and clear operating standards so each arrival is handled with the right context.",
  },
  {
    q: "Why Is A 24-Hour Lead Time Required?",
    a: "Premium execution requires enough time to shop accurately, coordinate access, and stock the home cleanly. The lead time protects quality.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

const coreOutcomes = [
  {
    title: "Guest Convenience",
    desc: "Guests can order before arrival so the stay starts with a stocked home instead of an errand list.",
  },
  {
    title: "Operator Consistency",
    desc: "Hosts and property managers get a repeatable arrival process that does not depend on internal coordination every time.",
  },
  {
    title: "Trusted Execution",
    desc: "In-house, trained, and vetted Bellhopt team members handle the final in-home work with property-specific instructions.",
  },
];

const trustPillars = [
  {
    title: "In-House Concierge Team",
    desc: "Bellhopt does not outsource the arrival experience to random last-mile labor. The team executing inside the home is part of Bellhopt.",
  },
  {
    title: "Trained And Vetted Staff",
    desc: "Every team member is trained on standards, presentation, access handling, and the details that affect guest trust and host confidence.",
  },
  {
    title: "Property-Specific Context",
    desc: "Access notes, lock details, entry instructions, substitutions, and home-specific preferences guide the execution for each stay.",
  },
  {
    title: "Five-Star Arrival Focus",
    desc: "The goal is not to drop bags. The goal is to create a clean, intentional, premium first impression that supports better reviews.",
  },
];

const trustBadges = [
  "In-House Team",
  "Trained And Vetted",
  "Property-Aware Execution",
  "Five-Star Arrival Focus",
];

const comparisonCards = [
  {
    title: "Typical Doorstep Delivery",
    tone: "neutral",
    items: [
      "Ends at the door instead of preparing the home",
      "Little or no property-specific context",
      "No real arrival presentation standard",
      "Often built around convenience for the courier, not the host",
    ],
  },
  {
    title: "Bellhopt Concierge Arrival",
    tone: "accent",
    items: [
      "Reservation-aware guest ordering before check-in",
      "In-home stocking and organization handled by Bellhopt",
      "Property instructions, access details, and arrival notes used in execution",
      "Designed around a premium first impression and a five-star stay",
    ],
  },
];

const hostSteps = [
  {
    title: "Connect Your Portfolio",
    desc: "Properties are onboarded through integration or manual setup. Bellhopt aligns property details, access information, and reservation timing so the team is working from the right data.",
  },
  {
    title: "Set Arrival Standards",
    desc: "You define the operating details that matter, including access instructions, entry notes, substitution preferences, and any expectations that affect how the home should be handled.",
  },
  {
    title: "Launch The Guest Ordering Flow",
    desc: "Guests receive a reservation-aware Bellhopt experience where they can shop ahead of arrival and place orders that match the stay.",
  },
  {
    title: "Bellhopt Executes In The Home",
    desc: "Bellhopt shops, delivers, stocks, and organizes based on the order and the property's instructions. This is handled by in-house, trained, vetted Bellhopt staff.",
  },
  {
    title: "Protect A Repeatable Brand Standard",
    desc: "The result is a more consistent arrival across homes, fewer manual handoffs for your team, and an experience that feels premium instead of improvised.",
  },
];

const guestSteps = [
  {
    title: "Receive A Reservation-Based Link",
    desc: "Guests are invited into a pre-arrival shopping flow tied to the right reservation and property.",
  },
  {
    title: "Shop Before Check-In",
    desc: "They can browse thousands of products and build the order before travel, rather than after arrival.",
  },
  {
    title: "Bellhopt Prepares The Home",
    desc: "Bellhopt coordinates shopping, delivery timing, access, and in-home placement before the guest walks in.",
  },
  {
    title: "Arrive To A Stocked, Organized Space",
    desc: "The first hour feels easy, polished, and ready to use. That is where the five-star arrival starts.",
  },
];

const premiumStandards = [
  {
    title: "Clean Placement",
    desc: "Items are put where they belong, not left in bags on the floor or stacked on the counter.",
  },
  {
    title: "Intentional Organization",
    desc: "Fridge, pantry, and countertop staging should feel neat, thoughtful, and aligned with a premium stay.",
  },
  {
    title: "Instruction-Driven Execution",
    desc: "Property access and in-home work are handled using the details provided for that home, not a one-size-fits-all routine.",
  },
  {
    title: "Consistency At Scale",
    desc: "Whether a host has one home or a large portfolio, Bellhopt is built to repeat the same arrival-ready standard.",
  },
];

const policyItems = [
  "24-Hour Lead Time Required",
  "Clear Access Protocols Before Execution",
  "Property Instructions Logged Before Arrival",
  "Substitution Preferences Used Where Available",
  "Communication Standards That Reduce Confusion For Guests And Hosts",
];

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function InfoCard({ title, desc, eyebrow, className = "" }) {
  return (
    <div className={`rounded-[28px] border border-[#E5E7EB] bg-white/90 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] ${className}`}>
      {eyebrow ? (
        <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">{eyebrow}</p>
      ) : null}
      <h3 className="font-segoe text-[20px] font-semibold text-[#111827]">{title}</h3>
      <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">{desc}</p>
    </div>
  );
}

function StepCard({ index, title, desc, theme = "warm" }) {
  const isCool = theme === "cool";

  return (
    <div
      className={`rounded-[30px] border p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] ${
        isCool
          ? "border-[#DBEAFE] bg-[linear-gradient(180deg,rgba(248,250,252,0.96),rgba(239,246,255,0.96))]"
          : "border-[#FEE2E2] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,245,245,0.98))]"
      }`}
    >
      <p className={`text-[12px] font-semibold tracking-[0.14em] ${isCool ? "text-[#1D4ED8]" : "text-[#EF4949]"}`}>
        Step 0{index}
      </p>
      <h3 className="mt-3 font-segoe text-[20px] font-semibold text-[#111827]">{title}</h3>
      <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">{desc}</p>
    </div>
  );
}

function ComparisonCard({ title, items, tone }) {
  const isAccent = tone === "accent";

  return (
    <div
      className={`rounded-[30px] border p-6 shadow-[0_14px_36px_rgba(15,23,42,0.05)] ${
        isAccent
          ? "border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,241,242,0.96))]"
          : "border-[#E5E7EB] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(249,250,251,0.96))]"
      }`}
    >
      <p className={`text-[12px] font-semibold tracking-[0.14em] ${isAccent ? "text-[#EF4949]" : "text-[#6B7280]"}`}>
        {isAccent ? "Premium Standard" : "Basic Model"}
      </p>
      <h3 className="mt-3 font-segoe text-[24px] font-semibold text-[#111827]">{title}</h3>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-[15px] leading-7 text-[#4B5563]">
            <span
              className={`mt-[7px] inline-flex h-2.5 w-2.5 rounded-full ${
                isAccent ? "bg-[#EF4949]" : "bg-[#9CA3AF]"
              }`}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HowItWorksPage() {
  const title = "How Bellhopt Works | In-House Concierge Grocery Delivery And Stocking";
  const description =
    "See how Bellhopt works for guests, hosts, and property managers. In-house, trained, vetted Bellhopt concierge teams deliver, stock, and organize homes for five-star arrivals.";
  const canonical = "https://bellhopt.com/how-it-works/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "How Bellhopt Works",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "How It Works", item: canonical },
      ],
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/how-it-works/"
        robots="index,follow"
        ogTitle="How Bellhopt Works"
        ogDescription="Bellhopt combines guest ordering with in-house, trained, vetted concierge execution to deliver five-star arrivals at scale."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} relative overflow-hidden p-8 mobile:p-5`}>
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-24 top-0 h-64 w-64 rounded-full bg-[#FFE4E6] blur-3xl opacity-70" />
            <div className="absolute bottom-0 left-0 h-56 w-56 rounded-full bg-[#FEE2E2] blur-3xl opacity-60" />
          </div>

          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-end">
            <div>
              <p className="text-[13px] font-semibold tracking-[0.14em] text-[#EF4949]">
                Premium Arrival Operations
              </p>
              <h1 className="mt-3 font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
                How Bellhopt Delivers Five-Star Arrivals
              </h1>
              <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
                Bellhopt combines a guest-friendly ordering flow with an in-house concierge
                execution layer that delivers, stocks, and organizes homes to a consistent
                arrival-ready standard. Hosts trust Bellhopt because the work inside the home is
                handled by trained, vetted Bellhopt team members who are accountable to the brand.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                  Get Started With Bellhopt
                </a>
                <Link to="/for/property-managers" className={SECONDARY_CTA}>
                  For Property Managers
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {trustBadges.map((badge) => (
                  <span
                    key={badge}
                    className="rounded-full border border-[#FECACA] bg-[#FFF5F5] px-4 py-2 text-[12px] font-semibold tracking-[0.06em] text-[#991B1B]"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-[13px] text-[#6B7280]">
                24-Hour Lead Time Required. Built For Single Homes, Multi-Market Operators, And
                Portfolios With 1,000+ Properties.
              </p>
            </div>

            <div className="rounded-[32px] border border-white/70 bg-white/85 p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <div className="rounded-[26px] border border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,245,245,0.95),rgba(255,255,255,0.92))] p-5">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                  The Bellhopt Standard
                </p>
                <h2 className="mt-3 font-segoe text-[26px] font-semibold leading-tight text-[#111827]">
                  Trusted In-Home Execution, Not Generic Last-Mile Delivery
                </h2>
                <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">
                  When someone enters a property before check-in, hosts need more than convenience.
                  They need a team they can trust to represent the home well.
                </p>
              </div>
              <div className="mt-5 grid gap-3">
                {[
                  {
                    title: "Bellhopt Team Members",
                    desc: "The final arrival experience is handled by Bellhopt, not passed to unknown third parties.",
                  },
                  {
                    title: "Home-Specific Context",
                    desc: "Property instructions, access notes, and execution details stay tied to the stay.",
                  },
                  {
                    title: "Premium Presentation",
                    desc: "Stocking and organization are designed to support a stronger first impression and better reviews.",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-[#E5E7EB] bg-white/92 px-4 py-4"
                  >
                    <h3 className="font-segoe text-[17px] font-semibold text-[#111827]">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-[14px] leading-6 text-[#6B7280]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              What Bellhopt Solves
            </h2>
            <p className="mt-3 max-w-[760px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Great arrivals do not happen by accident. Guests want convenience, operators need
              consistency, and both sides need to trust the team entering the home before check-in.
              Bellhopt is built to solve all three.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {coreOutcomes.map((item) => (
                <InfoCard
                  key={item.title}
                  title={item.title}
                  desc={item.desc}
                  eyebrow="Core Outcome"
                />
              ))}
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
                  Not Just Doorstep Delivery
                </h2>
                <p className="mt-3 max-w-[760px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                  The difference is not only what is delivered. It is how the home is prepared, who
                  is trusted to enter it, and whether the final arrival reflects the standard of the
                  property.
                </p>
              </div>
              <div className="rounded-2xl border border-[#E5E7EB] bg-white/90 px-4 py-3 text-[13px] font-semibold tracking-[0.08em] text-[#374151]">
                Arrival Experience Comparison
              </div>
            </div>
            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              {comparisonCards.map((card) => (
                <ComparisonCard
                  key={card.title}
                  title={card.title}
                  items={card.items}
                  tone={card.tone}
                />
              ))}
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
                  Why Bellhopt Feels Premium
                </h2>
                <p className="mt-3 max-w-[760px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                  Premium means the details are handled before the guest notices them. Bellhopt is
                  built for operators who care about the first impression, the review impact, and
                  the trust required to let a service provider work inside the home.
                </p>
              </div>
              <div className="rounded-2xl border border-[#E5E7EB] bg-white/90 px-4 py-3 text-[13px] font-semibold tracking-[0.08em] text-[#374151]">
                Built Around Trust
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {trustPillars.map((item) => (
                <InfoCard
                  key={item.title}
                  title={item.title}
                  desc={item.desc}
                  eyebrow="Trust Layer"
                />
              ))}
            </div>
          </section>

          <section id="for-hosts" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="grid gap-6 lg:grid-cols-[290px_minmax(0,1fr)]">
              <div className="rounded-[30px] border border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,245,245,0.96),rgba(255,255,255,0.94))] p-6">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                  Operator Flow
                </p>
                <h2 className="mt-3 font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827] leading-tight">
                  How It Works For Hosts And Property Managers
                </h2>
                <p className="mt-4 text-[16px] text-[#6B7280] leading-7">
                  Bellhopt is built to fit into an operator workflow without turning your team into
                  a concierge department. The guest flow stays simple while Bellhopt handles the
                  execution inside the home.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {hostSteps.map((item, index) => (
                  <StepCard
                    key={item.title}
                    index={index + 1}
                    title={item.title}
                    desc={item.desc}
                    theme="warm"
                  />
                ))}
              </div>
            </div>
          </section>

          <section id="for-guests" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="grid gap-6 lg:grid-cols-[290px_minmax(0,1fr)]">
              <div className="rounded-[30px] border border-[#DBEAFE] bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(239,246,255,0.96))] p-6">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#1D4ED8]">
                  Guest Flow
                </p>
                <h2 className="mt-3 font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827] leading-tight">
                  How It Works For Guests
                </h2>
                <p className="mt-4 text-[16px] text-[#6B7280] leading-7">
                  For the guest, the experience should feel easy. For the host, it should feel
                  reliable. Bellhopt is designed so both are true at the same time.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {guestSteps.map((item, index) => (
                  <StepCard
                    key={item.title}
                    index={index + 1}
                    title={item.title}
                    desc={item.desc}
                    theme="cool"
                  />
                ))}
              </div>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              What Makes The Arrival Feel Five-Star
            </h2>
            <p className="mt-3 max-w-[760px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              The Bellhopt standard is about how the home feels when the guest enters. That means
              execution, presentation, and attention to detail matter as much as the products in the
              order.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {premiumStandards.map((item) => (
                <InfoCard key={item.title} title={item.title} desc={item.desc} />
              ))}
            </div>
          </section>

          <section id="in-house-team" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_330px]">
              <div>
                <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
                  What In-House Execution Means
                </h2>
                <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                  Bellhopt is not built on a faceless contractor model. Hosts and property managers
                  are trusting Bellhopt with access to real homes, real reservations, and real guest
                  expectations. That is why the execution layer matters.
                </p>
                <ul className="list-disc pl-6 mt-5 text-[17px] mobile:text-[15px] text-[#6B7280] leading-8">
                  <li>Bellhopt team members are trained on access handling and in-home standards.</li>
                  <li>Bellhopt team members are vetted before they represent the brand in a property.</li>
                  <li>Execution is guided by property notes, entry instructions, and arrival standards.</li>
                  <li>The goal is to give hosts confidence, not introduce another point of risk.</li>
                </ul>
              </div>
              <div className="rounded-[30px] border border-[#111827] bg-[#111827] p-6 text-white shadow-[0_20px_50px_rgba(15,23,42,0.18)]">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#FCA5A5]">
                  Trust Signal
                </p>
                <h3 className="mt-3 font-segoe text-[28px] font-semibold leading-tight">
                  Hosts Know Who Is Entering The Home And What Standard They Are Held To
                </h3>
                <p className="mt-4 text-[15px] leading-7 text-[#D1D5DB]">
                  That clarity is part of what makes Bellhopt feel premium. Trust is operational,
                  not abstract.
                </p>
              </div>
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="rounded-[32px] border border-[#FECACA] bg-[linear-gradient(135deg,rgba(255,245,245,0.96),rgba(255,255,255,0.94),rgba(254,226,226,0.72))] p-6 mobile:p-5">
              <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                Premium CTA
              </p>
              <h2 className="mt-3 font-segoe font-semibold text-[34px] mobile:text-[26px] text-[#111827] leading-tight">
                Make Arrival Effortless
              </h2>
              <p className="mt-4 max-w-[760px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                A premium guest arrival requires more than delivery. Bellhopt provides the ordering
                flow, the trusted in-home execution, and the consistent standards operators need to
                make five-star arrivals repeatable.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                  Get Started With Bellhopt
                </a>
                <Link to="/for/property-managers" className={SECONDARY_CTA}>
                  For Property Managers
                </Link>
              </div>
            </div>
          </section>

          <Section id="policies" title="Policies That Protect Quality">
            <p>
              Bellhopt keeps a simple operating structure because premium execution depends on clear
              inputs and enough time to do the work correctly.
            </p>
            <ul className="list-disc pl-6 mt-4">
              {policyItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Related Pages
            </h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/pre-arrival-groceries">Pre-Arrival Groceries</Link></li>
              <li><Link className="text-primary" to="/vacation-rental-grocery-delivery">Vacation Rental Grocery Delivery</Link></li>
              <li><Link className="text-primary" to="/airbnb-grocery-stocking">Airbnb Grocery Stocking</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
              <li><Link className="text-primary" to="/for/property-managers">For Property Managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default HowItWorksPage;
